import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { newsArticles as fallback } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { api } from '../services/api';
import HutRiWatermark from '../components/HutRiWatermark';

const NewsDetail = () => {
  const { slug } = useParams();
  const { t, tv, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setActiveImg(0);
    api.getNews(slug)
      .then((d) => setArticle(d))
      .catch(() => {
        const f = fallback.find((n) => n.slug === slug || String(n.id) === slug);
        setArticle(f);
      });
    api.listNews().then((all) => setRelated(all.slice(0, 3))).catch(() => setRelated(fallback.slice(0, 3)));
  }, [slug]);

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-gray-500">{lang === 'id' ? 'Memuat berita...' : 'Loading article...'}</p>
      </div>
    );
  }

  return (
    <>
      <div className="relative h-[420px] bg-[#0a1929] overflow-hidden">
        <img src={article.image} alt={tv(article.title)} className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1929]/40 via-[#0a1929]/70 to-[#0a1929]" />
        <HutRiWatermark size="md" />
        <div className="relative max-w-4xl mx-auto px-4 h-full flex flex-col justify-end pb-10">
          <Link to="/berita" className="inline-flex items-center gap-2 text-[#d4a017] hover:text-white text-sm mb-4 self-start">
            <ArrowLeft size={16} /> {t('backToNews')}
          </Link>
          <div className="inline-flex items-center gap-3 mb-3">
            <span className="bg-[#d4a017] text-[#0a1929] text-xs font-bold px-3 py-1 tracking-widest">{article.category}</span>
            <span className="text-white/80 text-xs flex items-center gap-1"><Calendar size={12} /> {article.date}</span>
            <span className="text-white/80 text-xs flex items-center gap-1"><User size={12} /> {article.author || 'Humas YPPSDP'}</span>
          </div>
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{tv(article.title)}</h1>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-6 text-xs text-gray-500 flex items-center gap-3 pb-4 border-b">
          <span>{t('published')}: <strong className="text-[#0a1929]">{article.date}</strong></span>
          <span>|</span>
          <span>{t('by')}: <strong className="text-[#0a1929]">{article.author || 'Humas YPPSDP'}</strong></span>
          <span className="ml-auto flex items-center gap-2">
            <Share2 size={14} />
            <a href="#" className="hover:text-[#0a1929]"><Facebook size={14} /></a>
            <a href="#" className="hover:text-[#0a1929]"><Twitter size={14} /></a>
            <a href="#" className="hover:text-[#0a1929]"><Linkedin size={14} /></a>
          </span>
        </div>
        <p className="text-xl text-gray-700 leading-relaxed mb-8 font-light border-l-4 border-[#d4a017] pl-4 italic">{tv(article.excerpt)}</p>
        <div className="prose max-w-none text-gray-800 leading-relaxed space-y-5">
          {tv(article.content).split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
        </div>

        {article.gallery && article.gallery.length > 0 && (
          <div className="mt-12">
            <div className="h-[3px] w-14 bg-[#d4a017] mb-3" />
            <h3 className="text-2xl font-bold text-[#0a1929] mb-5" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{t('gallery')}</h3>
            <div className="aspect-video bg-[#0a1929] mb-3 overflow-hidden">
              <img src={article.gallery[activeImg]} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {article.gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-video overflow-hidden border-2 transition ${activeImg === i ? 'border-[#d4a017]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </article>

      <section className="bg-[#f5f5f5] py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-[3px] w-14 bg-[#d4a017] mb-3" />
          <h3 className="text-2xl font-bold text-[#0a1929] mb-6" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{t('relatedNews')}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {related.filter((r) => (r.slug || r.id) !== slug).slice(0, 3).map((n) => (
              <Link key={n.id} to={`/berita/${n.slug || n.id}`} className="bg-white border border-gray-200 hover:border-[#d4a017] hover:shadow transition group">
                <div className="aspect-[16/10] overflow-hidden bg-[#0a1929]">
                  <img src={n.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-bold text-[#d4a017] tracking-widest mb-1">{n.category}</div>
                  <div className="font-bold text-[#0a1929] leading-snug" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{tv(n.title)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default NewsDetail;
